import React, { useState, useContext, useEffect } from "react";
import { OrderContext } from "../../contexts/OrderContext";
import axios from "axios";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Autocomplete,
} from "@mui/material";
import Papa from "papaparse"; // CSV parsing library
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";

const CreateUser = () => {
  const { storeData, fetchStoreData } = useContext(OrderContext);

  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state || {};
  const isFromNonExisting = Boolean(navState?.mobile);

  const [csvData, setCsvData] = useState(null); // State for CSV data
  const userRole = localStorage.getItem("userRole");

  const { mfrstore = [] } = storeData;
  const selectedMfr = JSON.parse(localStorage.getItem("selectedMfr"));

  const mfr_id =
    userRole === "COMFR" ||
    userRole === "SUPERADMIN" ||
    userRole === "DIGITAL MARKETER"
      ? selectedMfr?.id
      : localStorage.getItem("id");

  const [user, setUser] = useState({
    name: "",
    mobile: "",
    otp: "",
    role: "Customer",
    status: "1",
    store_id: "",
  });
  const [isCreatingStore, setIsCreatingStore] = useState(true);
  const [newStore, setNewStore] = useState({ name: "", place: "" });
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Auto fill the form for non-existing users
  useEffect(() => {
    if (!navState?.mobile) return;

    setUser((prev) => ({
      ...prev,
      mobile: navState.mobile,
      store_id: navState.store_id || "",
    }));

    // Force "Use Existing Store"
    if (navState.store_id) {
      setIsCreatingStore(false);
    }
  }, [navState]);

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleStoreChange = (e) => {
    setUser({ ...user, store_id: e.target.value });
  };

  const handleNewStoreChange = (e) => {
    setNewStore({ ...newStore, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (fileExtension === "csv") {
      Papa.parse(file, {
        complete: (result) => {
          const rows = result.data;
          const users = rows.slice(1).map((row) => ({
            name: row[0],
            mobile: row[1],
            role: row[2],
            store_name: row[3],
            store_place: row[4],
          }));
          setCsvData(users);
        },
        header: false,
        skipEmptyLines: true,
      });
    } else if (["xlsx", "xls", "xlsb"].includes(fileExtension)) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const users = json.slice(1).map((row) => ({
          name: row[0],
          mobile: row[1],
          role: row[2],
          store_name: row[3],
          store_place: row[4],
        }));
        setCsvData(users);
      };
      reader.readAsArrayBuffer(file);
    } else {
      alert(
        "Unsupported file type. Please upload a .csv, .xlsx, .xls, or .xlsb file.",
      );
    }
  };

  const handleBulkSave = async () => {
    if (!csvData || csvData.length === 0) return;

    setLoading(true);
    try {
      const payload = {
        users: csvData,
        mfr_id,
      };

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/bulkcreateusers`,
        payload,
      );
      setSnackbar({
        open: true,
        message: "Users imported successfully!",
        severity: "success",
      });
      setCsvData(null); // Reset after success
      fetchStoreData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to import users.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let requestData;
      let apiPath;

      if (userRole === "DIGITAL MARKETER") {
        // Special handling for DIGITAL MARKETER role
        requestData = isCreatingStore
          ? {
              name: user.name,
              mobile: user.mobile,
              role: user.role,
              new_store: true, // Flag indicating new store creation
              store_name: newStore.name,
              store_place: newStore.place,
              // store_prefix: newStore.prefix,
              mfr_id,
            }
          : {
              name: user.name,
              mobile: user.mobile,
              role: user.role,
              otp: Number(user.otp),
              status: user.status,
              new_store: false,
              store_id: user.store_id,
              mfr_id,
            };

        apiPath = "/api/fetchorder/createuser";
      } else {
        // Existing logic for other roles
        requestData = isCreatingStore
          ? {
              name: user.name,
              mobile: user.mobile,
              role: user.role,
              new_store: true, // Flag indicating new store creation
              store_name: newStore.name,
              store_place: newStore.place,
              // store_prefix: newStore.prefix,
              mfr_id,
              otp: Number(user.otp),
            }
          : {
              name: user.name,
              mobile: user.mobile,
              role: user.role,
              otp: Number(user.otp),
              status: user.status,
              new_store: false,
              store_id: user.store_id,
              mfr_id,
            };

        apiPath =
          userRole === "SEMIMFR"
            ? "/api/fetchorder/createSemimfrUser"
            : "/api/fetchorder/createuser";
      }

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${apiPath}`,
        requestData,
      );

      setSnackbar({
        open: true,
        message: "User created successfully!",
        severity: "success",
      });

      fetchStoreData();

      // Update Non-Existing User & Redirect
      if (isFromNonExisting) {
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/non-existing-users`,
          {
            mobile: user.mobile,
            mfr_id,
          },
        );

        setTimeout(() => {
          navigate(-1, {
            state: {
              refreshNonExisting: true,
              successMessage: "Customer created successfully",
            },
          });
        }, 800);
      }

      setUser({
        name: "",
        mobile: "",
        role: user?.role,
        store_id: "",
        status: "1",
        otp: "",
      });
      setNewStore({ name: "", place: "", prefix: "" });
      setIsCreatingStore(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response.data.message,
        severity: "error",
      });
    }

    setLoading(false);
  };

  return (
    <Box p={2}>
      <Grid
        container
        spacing={4}
        justifyContent="center"
        alignItems="flex-start"
      >
        {/* Manual Create User Form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ padding: 3, boxShadow: 3 }}>
            <CardContent>
              {/* Heading */}
              <Typography
                variant="h5"
                fontWeight="bold"
                textAlign="center"
                mb={2}
              >
                Create User
              </Typography>

              {/* Create User Form */}
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* User Name */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="User Name"
                      name="name"
                      value={user.name}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Mobile Number */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      name="mobile"
                      value={user.mobile}
                      onChange={handleChange}
                      required
                      disabled={Boolean(navState?.mobile)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* OTP */}
                  <Grid item xs={12}>
                    <TextField
                      type="number"
                      fullWidth
                      label="OTP"
                      name="otp"
                      value={user.otp}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Status Dropdown */}
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel shrink>Status</InputLabel>
                      <Select
                        label="Status"
                        name="status"
                        value={user.status}
                        onChange={handleChange}
                      >
                        <MenuItem value="1">Active</MenuItem>
                        <MenuItem value="0">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Role Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel shrink>Role</InputLabel>
                      <Select
                        disabled={userRole === "DIGITAL MARKETER"}
                        label="Role"
                        name="role"
                        value={user.role}
                        onChange={handleChange}
                      >
                        <MenuItem value="Customer">Customer</MenuItem>
                        {userRole !== "SEMIMFR" && [
                          <MenuItem key="Karigar" value="Karigar">
                            Karigar
                          </MenuItem>,
                          <MenuItem key="SEMIMFR" value="SEMIMFR">
                            SEMIMFR
                          </MenuItem>,
                          <MenuItem key="COMFR" value="COMFR">
                            MFR
                          </MenuItem>,
                          <MenuItem
                            key="DIGITAL MARKETER"
                            value="DIGITAL MARKETER"
                          >
                            DIGITAL MARKETER
                          </MenuItem>,
                          <MenuItem key="WAREHOUSE" value="WAREHOUSE">
                            WAREHOUSE
                          </MenuItem>,
                          <MenuItem key="QC" value="QC">
                            QC
                          </MenuItem>,
                          <MenuItem key="DIGITAL ORDER" value="DIGITAL ORDER">
                            DIGITAL ORDER
                          </MenuItem>,
                          <MenuItem key="ORDER" value="ORDER">
                            ORDER
                          </MenuItem>,
                          // <MenuItem key="SALES" value="SALES">
                          //   SALES
                          // </MenuItem>,
                        ]}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Store Dropdown */}
                  {!isCreatingStore && (
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        options={mfrstore}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Store"
                            required
                          />
                        )}
                        value={
                          mfrstore.find(
                            (store) => store.id === user.store_id,
                          ) || null
                        }
                        onChange={(_, newValue) =>
                          setUser({
                            ...user,
                            store_id: newValue ? newValue.id : "",
                          })
                        }
                      />
                    </Grid>
                  )}

                  {/* Toggle Button */}

                  <>
                    {" "}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setIsCreatingStore(!isCreatingStore)}
                      >
                        {isCreatingStore
                          ? "Use Existing Store"
                          : "Create New Store"}
                      </Button>
                    </Grid>
                    {/* New Store Fields */}
                    {isCreatingStore && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Store Name"
                            name="name"
                            value={newStore.name}
                            onChange={handleNewStoreChange}
                            required
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Store Place"
                            name="place"
                            value={newStore.place}
                            onChange={handleNewStoreChange}
                            required
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </>
                    )}
                  </>

                  {/* Submit */}
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Bulk Upload */}
        {userRole !== "SEMIMFR" && (
          <>
            <Grid item xs={12} md={6}>
              <Card sx={{ padding: 3, boxShadow: 3, marginBottom: 10 }}>
                <CardContent>
                  <Typography variant="h6" textAlign="center" mb={2}>
                    Import Users from File
                  </Typography>

                  <Button variant="contained" component="label" fullWidth>
                    Upload File (.csv, .xlsx, .xls, .xlsb)
                    <input
                      type="file"
                      accept=".csv, .xlsx, .xls, .xlsb"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>

                  {csvData?.length > 0 && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleBulkSave}
                      fullWidth
                      sx={{ mt: 2 }}
                      disabled={loading} //  Disable when loading
                    >
                      {loading
                        ? "Saving..."
                        : `Save ${csvData.length} Imported Users`}{" "}
                      {/* Show loading text */}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "100px !important", // force override snackbar position
          },
        }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateUser;
